import { ZoneData } from "@/components/explore/ExploreContext";
import { findClosestTimeIndex } from "@/components/explore/Heatmap";
import { Badge } from "@/components/ui/badge";
import { useSessionData } from "@/stores/SessionDataContext";
import { Activity, AlertTriangle, CheckCircle2 } from "lucide-react";
import { useMemo } from "react";

export type HealthStatus = "idle" | "optimal" | "moderate" | "critical";

interface HealthCheckResult {
  status: HealthStatus;
  label: string;
  description: string;
}

function calculateHealthStatus(
  zones: ZoneData[],
  times: string[],
  timeZone?: string
): HealthCheckResult {
  if (!zones || zones.length === 0 || !times || times.length === 0) {
    return {
      status: "optimal",
      label: "No Data",
      description: "Unable to determine current load",
    };
  }

  const currentTimeIndex = findClosestTimeIndex(times, timeZone);

  const startIndex = Math.max(0, currentTimeIndex - 1);
  const endIndex = Math.min(times.length - 1, currentTimeIndex + 1);

  let totalValue = 0;
  let maxValue = -Infinity;
  let count = 0;

  zones.forEach((zone) => {
    let allValues: number[] = [];
    zone.values.forEach((value) => {
      if (value > 0) {
        allValues.push(value);
      }
    });

    for (let i = startIndex; i <= endIndex; i++) {
      const value = zone.values[i];
      if (value !== undefined && value > 0) {
        totalValue += value;
        maxValue = Math.max(maxValue, value);
        count++;
      }
    }
  });

  if (count === 0) {
    return {
      status: "idle",
      label: "Idle",
      description: "No current load detected",
    };
  }

  const avgValue = totalValue / count;

  const allValues: number[] = [];
  zones.forEach((zone) => {
    zone.values.forEach((value) => {
      if (value > 0) {
        allValues.push(value);
      }
    });
  });

  if (allValues.length === 0) {
    return {
      status: "idle",
      label: "Idle",
      description: "No load data available",
    };
  }

  allValues.sort((a, b) => a - b);
  const p50 = allValues[Math.floor(allValues.length * 0.5)];
  const p75 = allValues[Math.floor(allValues.length * 0.75)];
  const p90 = allValues[Math.floor(allValues.length * 0.9)];

  if (avgValue >= p90 || maxValue >= p90) {
    return {
      status: "critical",
      label: "Critical",
      description: "Current load is very high",
    };
  } else if (avgValue >= p75 || maxValue >= p75) {
    return {
      status: "moderate",
      label: "Moderate",
      description: "Current load is elevated",
    };
  } else if (avgValue >= p50 * 0.1 || maxValue >= p50 * 0.1) {
    return {
      status: "optimal",
      label: "Optimal",
      description: "Current load is normal",
    };
  } else {
    return {
      status: "idle",
      label: "Idle",
      description: "Minimal or no current activity",
    };
  }
}

interface HealthCheckBadgeProps {
  zones: ZoneData[];
  times: string[];
}

export function HealthCheckBadge({ zones, times }: HealthCheckBadgeProps) {
  const { airport_config } = useSessionData();
  const healthCheck = useMemo(
    () =>
      calculateHealthStatus(zones, times, airport_config.timezone),
    [zones, times, airport_config.timezone]
  );

  const getStatusStyles = (status: HealthStatus) => {
    switch (status) {
      case "idle":
        return {
          className:
            "bg-slate-500/10 text-slate-600 hover:bg-slate-500/20 border-slate-500/20",
          icon: CheckCircle2,
        };
      case "optimal":
        return {
          className:
            "bg-green-500/10 text-green-600 hover:bg-green-500/20 border-green-500/20",
          icon: CheckCircle2,
        };
      case "moderate":
        return {
          className:
            "bg-yellow-500/10 text-yellow-600 hover:bg-yellow-500/20 border-yellow-500/20",
          icon: Activity,
        };
      case "critical":
        return {
          className:
            "bg-red-500/10 text-red-600 hover:bg-red-500/20 border-red-500/20",
          icon: AlertTriangle,
        };
    }
  };

  const { className, icon: Icon } = getStatusStyles(healthCheck.status);

  return (
    <Badge
      variant="outline"
      className={`${className} flex items-center gap-1.5 px-3 py-1 text-xs font-medium`}
      title={healthCheck.description}
    >
      <Icon className="h-3.5 w-3.5" />
      <span>{healthCheck.label}</span>
    </Badge>
  );
}
